import { useEffect, useMemo, useState } from 'react'
import { config } from './config'
import {
  DEFAULTS,
  type BlacklistDim, type BlacklistRule, type BlacklistTerm,
  type Params, type VocabEntry, type Vocabulary,
} from './domain'
import { modifiedCount } from './predictionRequest'

const BLACKLIST_DIMS: { dim: BlacklistDim; label: string }[] = [
  { dim: 'color', label: 'Colors' },
  { dim: 'interior', label: 'Interiors' },
  { dim: 'trim', label: 'Trims' },
  { dim: 'rim', label: 'Rims' },
  { dim: 'package', label: 'Packages' },
  { dim: 'single_option', label: 'Options' },
  { dim: 'model_code', label: 'Models' },
]
const DIM_LABEL: Record<BlacklistDim, string> = {
  color: 'Color', interior: 'Interior', rim: 'Rim', trim: 'Trim',
  package: 'Package', single_option: 'Option', model_code: 'Model',
}
// One-per-order dimensions: two values of these can never co-occur, so an AND
// combo over them is impossible. Packages / single options can appear together.
const SCALAR_DIMS = new Set<BlacklistDim>(['color', 'interior', 'rim', 'trim', 'model_code'])

// ── NMC toggle ────────────────────────────────────────────────────────────────

export function NmcToggle({ nmcMode, onChange }: { nmcMode: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-0 border border-[#CFD3DB] bg-white p-0.5 text-xs font-bold">
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-3 py-1 uppercase tracking-[0.04em] transition ${!nmcMode ? 'bg-bmw-ocean text-white' : 'text-[#666] hover:text-black'}`}
      >
        Legacy
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-3 py-1 uppercase tracking-[0.04em] transition ${nmcMode ? 'bg-bmw-ocean text-white' : 'text-[#666] hover:text-black'}`}
      >
        NMC
      </button>
    </div>
  )
}

// ── Parameter panel sub-components ───────────────────────────────────────────

type SliderRowProps = {
  label: string
  hint: string
  value: number
  min: number
  max: number
  step: number
  format?: (v: number) => string
  isModified?: boolean
  leftLabel?: string
  rightLabel?: string
  onChange: (v: number) => void
}

function SliderRow({ label, hint, value, min, max, step, format, isModified, leftLabel, rightLabel, onChange }: SliderRowProps) {
  const display = format ? format(value) : String(value)
  return (
    <div>
      <div className="flex items-baseline justify-between mb-0.5">
        <span className={`text-xs font-bold uppercase tracking-[0.06em] ${isModified ? 'text-bmw-ocean' : 'text-[#444]'}`}>
          {label}{isModified && <span className="ml-1 text-bmw-blue-grey">·</span>}
        </span>
        <span className="text-xs tabular-nums font-mono text-black text-right shrink-0 ml-2">{display}</span>
      </div>
      <p className="text-xs text-[#999] mb-1.5">{hint}</p>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 cursor-pointer accent-bmw-ocean"
      />
      {(leftLabel || rightLabel) && (
        <div className="flex justify-between mt-0.5">
          <span className="text-[10px] text-[#999]">{leftLabel}</span>
          <span className="text-[10px] text-[#999]">{rightLabel}</span>
        </div>
      )}
    </div>
  )
}

type SectionProps = { title: string; children: React.ReactNode }
function Section({ title, children }: SectionProps) {
  return (
    <div>
      <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.12em] text-[#777]">{title}</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  )
}

type ParamsPanelProps = {
  params: Params
  onChange: (p: Params) => void
  onReset: () => void
}

export function ParamsPanel({ params, onChange, onReset }: ParamsPanelProps) {
  function set<K extends keyof Params>(key: K, value: Params[K]) {
    onChange({ ...params, [key]: value })
  }

  const [showAdvanced, setShowAdvanced] = useState(false)
  const modified = modifiedCount(params)

  return (
    <div className="mt-4 border border-[#E5E7EC] bg-[#FAFBFC] p-5 space-y-6">
      {/* The three levers users actually turn — framed by what each end does. */}
      <Section title="Prediction basis">
        <SliderRow
          label="Demand signal"
          hint="Which orders to trust: real customer orders (BTO) vs dealer stock orders (BTS)."
          value={params.btoVsBtsBalance} min={0} max={1} step={0.05}
          format={v => v <= 0.02 ? 'Customer' : v >= 0.98 ? 'Stock' : `${Math.round((1 - v) * 100)}/${Math.round(v * 100)}`}
          leftLabel="← Customer orders"
          rightLabel="Stock orders →"
          isModified={params.btoVsBtsBalance !== DEFAULTS.btoVsBtsBalance}
          onChange={v => set('btoVsBtsBalance', v)}
        />
        <SliderRow
          label="Recency"
          hint="How fast older orders lose influence. Left = recent orders dominate; right = older history still counts."
          value={params.recencyHalfLifeDays} min={30} max={730} step={30}
          format={v => `${v}d`}
          leftLabel="← Recent-heavy"
          rightLabel="All history →"
          isModified={params.recencyHalfLifeDays !== DEFAULTS.recencyHalfLifeDays}
          onChange={v => set('recencyHalfLifeDays', v)}
        />
        <SliderRow
          label="Spec coverage"
          hint="How much of the demand to cover with package bundles. Left = only the most common specs; right = include rarer configurations too."
          value={params.blockCoverageTarget} min={0.50} max={1.00} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`}
          leftLabel="← Common only"
          rightLabel="Include rare →"
          isModified={params.blockCoverageTarget !== DEFAULTS.blockCoverageTarget}
          onChange={v => set('blockCoverageTarget', v)}
        />
        <SliderRow
          label="Variant detail"
          hint="How rare a colour/interior/rim combination can be and still get its own row within a bundle. Left includes rare variants; right keeps only common ones."
          value={params.minRowShare} min={0.01} max={0.30} step={0.01}
          format={v => `${(v * 100).toFixed(0)}%`}
          leftLabel="← Include rare"
          rightLabel="Common only →"
          isModified={params.minRowShare !== DEFAULTS.minRowShare}
          onChange={v => set('minRowShare', v)}
        />
      </Section>

      {/* Advanced — engine tuning; defaults are right for normal use. */}
      <div className="border-t border-[#E5E7EC] pt-4">
        <button
          type="button"
          onClick={() => setShowAdvanced(v => !v)}
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.06em] text-[#666] hover:text-black"
        >
          <span className="text-[10px]">{showAdvanced ? '▲' : '▼'}</span>
          Advanced
          <span className="font-normal normal-case tracking-normal text-[#999]">— engine tuning; defaults are fine for normal use</span>
        </button>
      </div>

      {showAdvanced && <>
      <Section title="Confidence">
        <SliderRow
          label="ESS threshold"
          hint="Effective sample size for 50% confidence. Lower = more sensitive to thin data."
          value={params.essThreshold} min={10} max={500} step={10}
          isModified={params.essThreshold !== DEFAULTS.essThreshold}
          onChange={v => set('essThreshold', v)}
        />
        <SliderRow
          label="Single option low threshold"
          hint="Options below this frequency are always excluded. From here to 80%, gap detection decides. Above 80%, always included."
          value={params.singleOptionThreshold} min={0.05} max={0.75} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`}
          isModified={params.singleOptionThreshold !== DEFAULTS.singleOptionThreshold}
          onChange={v => set('singleOptionThreshold', v)}
        />
      </Section>

      <Section title="Similarity weighting">
        <SliderRow
          label="Package weight"
          hint="Boost for orders with matching package bundles — the primary conditioning signal."
          value={params.pkgWeight} min={0} max={5} step={0.5}
          isModified={params.pkgWeight !== DEFAULTS.pkgWeight}
          onChange={v => set('pkgWeight', v)}
        />
        <SliderRow
          label="Color weight"
          hint="Extra boost when historical order matches the chosen color."
          value={params.colorWeight} min={0} max={3} step={0.25}
          isModified={params.colorWeight !== DEFAULTS.colorWeight}
          onChange={v => set('colorWeight', v)}
        />
        <SliderRow
          label="Interior weight"
          hint="Extra boost when historical order matches the chosen interior."
          value={params.interiorWeight} min={0} max={3} step={0.25}
          isModified={params.interiorWeight !== DEFAULTS.interiorWeight}
          onChange={v => set('interiorWeight', v)}
        />
        <SliderRow
          label="Rim weight"
          hint="Extra boost when historical order matches the chosen rim."
          value={params.rimWeight} min={0} max={3} step={0.25}
          isModified={params.rimWeight !== DEFAULTS.rimWeight}
          onChange={v => set('rimWeight', v)}
        />
      </Section>

      <Section title="Option blocks">
        <label className="flex cursor-pointer items-start gap-2 sm:col-span-2">
          <input
            type="checkbox" checked={params.enableOptionClusters}
            onChange={e => set('enableOptionClusters', e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-bmw-ocean"
          />
          <span>
            <span className={`text-xs font-bold uppercase tracking-[0.06em] ${params.enableOptionClusters ? 'text-bmw-ocean' : 'text-[#444]'}`}>
              Split bundles by optional accessories
            </span>
            <p className="text-xs text-[#999]">One block per package becomes several that share the packages but differ by their co-occurring options. The absence of an option competes on equal footing with its presence.</p>
          </span>
        </label>
        {params.enableOptionClusters && <>
          <SliderRow
            label="Option floor"
            hint="An optional accessory joins a block's signature only if it's on at least this share of the bundle's cars. Rare options stay hidden."
            value={params.optionMinSignatureShare} min={0.02} max={0.40} step={0.01}
            format={v => `${(v * 100).toFixed(0)}%`}
            isModified={params.optionMinSignatureShare !== DEFAULTS.optionMinSignatureShare}
            onChange={v => set('optionMinSignatureShare', v)}
          />
          <SliderRow
            label="Option coverage target"
            hint="Keep adding option-variant blocks per bundle until they cover this share of the bundle's demand."
            value={params.optionCoverageTarget} min={0.50} max={1.00} step={0.05}
            format={v => `${(v * 100).toFixed(0)}%`}
            isModified={params.optionCoverageTarget !== DEFAULTS.optionCoverageTarget}
            onChange={v => set('optionCoverageTarget', v)}
          />
          <SliderRow
            label="Max blocks per bundle"
            hint="Hard cap on how many option-variant blocks a single package bundle can produce."
            value={params.maxOptionBlocksPerBundle} min={1} max={6} step={1}
            isModified={params.maxOptionBlocksPerBundle !== DEFAULTS.maxOptionBlocksPerBundle}
            onChange={v => set('maxOptionBlocksPerBundle', v)}
          />
          <SliderRow
            label="Min block share"
            hint="An option-variant block must be worth at least this share of TOTAL demand (bundle share × option-string share) to be split out. Stops a thin bundle from fragmenting into tiny blocks."
            value={params.minBlockShare} min={0.01} max={0.20} step={0.01}
            format={v => `${(v * 100).toFixed(0)}%`}
            isModified={params.minBlockShare !== DEFAULTS.minBlockShare}
            onChange={v => set('minBlockShare', v)}
          />
          <SliderRow
            label="Cross-option borrowing"
            hint="How much cars with other option strings inform a block's colors/rims and its confidence. 0 = only exact-match cars (strict, noisier); higher = borrow more from the bundle."
            value={params.optionCrossWeight} min={0} max={1} step={0.05}
            format={v => v === 0 ? 'off' : `${(v * 100).toFixed(0)}%`}
            isModified={params.optionCrossWeight !== DEFAULTS.optionCrossWeight}
            onChange={v => set('optionCrossWeight', v)}
          />
        </>}
      </Section>
      </>}

      {modified > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-[#666] hover:text-black underline underline-offset-2"
          >
            Reset {modified} change{modified !== 1 ? 's' : ''} to defaults
          </button>
        </div>
      )}
    </div>
  )
}

// ── Blacklist panel ───────────────────────────────────────────────────────────

// stable identity for a term / rule, for de-duplication and keys
function termKey(t: BlacklistTerm): string {
  return `${t.dimension}:${t.value}`
}
function ruleKey(r: BlacklistRule): string {
  return r.terms.map(termKey).sort().join('&')
}

// Descriptions from option_descriptions / rim_codes are stored as "<code> - <name>",
// so strip a leading code prefix to avoid showing the code twice.
export function cleanDesc(code: string, desc: string | null): string | null {
  if (!desc) return null
  let d = desc.trim()
  if (d.toLowerCase().startsWith(code.toLowerCase())) {
    d = d.slice(code.length).replace(/^[\s\-·:|]+/, '').trim()
  }
  return d || null
}

export function BlacklistPanel({ modelCodes, blacklist, onChange }: {
  modelCodes: string[]
  blacklist: BlacklistRule[]
  onChange: (rules: BlacklistRule[]) => void
}) {
  const [vocab, setVocab] = useState<Vocabulary | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [staging, setStaging] = useState<BlacklistTerm[]>([])

  const codesKey = modelCodes.join(',')
  useEffect(() => {
    if (!modelCodes.length) { setVocab(null); return }
    let live = true
    setVocab(null); setErr(null)
    fetch(`${config.apiUrl}/api/vocabulary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model_codes: modelCodes }),
    })
      .then(r => r.json())
      .then(d => { if (live) setVocab(d.categories) })
      .catch(() => { if (live) setErr('Could not load options') })
    return () => { live = false }
  }, [codesKey])

  // Filter each dimension's entries by code or description (case-insensitive).
  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    if (!vocab) return null
    const out: { dim: BlacklistDim; label: string; entries: VocabEntry[] }[] = []
    for (const { dim, label } of BLACKLIST_DIMS) {
      const entries = (vocab[dim] ?? []).filter(e =>
        !q || e.code.toLowerCase().includes(q) || (e.description ?? '').toLowerCase().includes(q))
      if (entries.length) out.push({ dim, label, entries })
    }
    return out
  }, [vocab, q])

  const stagingKeys = new Set(staging.map(termKey))
  const existingRuleKeys = new Set(blacklist.map(ruleKey))

  function toggleStaging(dim: BlacklistDim, value: string) {
    const t: BlacklistTerm = { dimension: dim, value }
    setStaging(s => s.some(x => termKey(x) === termKey(t))
      ? s.filter(x => termKey(x) !== termKey(t))
      : [...s, t])
  }
  // Default: each staged value becomes its own rule → excluded independently (OR).
  function addSeparate() {
    if (!staging.length) return
    const merged = [...blacklist]
    const seen = new Set(existingRuleKeys)
    for (const t of staging) {
      const r: BlacklistRule = { terms: [t] }
      const k = ruleKey(r)
      if (!seen.has(k)) { merged.push(r); seen.add(k) }
    }
    onChange(merged)
    setStaging([])
  }
  // Combo: one AND rule → excludes only cars that have all staged values together.
  function addCombo() {
    if (!staging.length) return
    const rule: BlacklistRule = { terms: staging }
    if (!existingRuleKeys.has(ruleKey(rule))) onChange([...blacklist, rule])
    setStaging([])
  }
  function removeRule(key: string) {
    onChange(blacklist.filter(r => ruleKey(r) !== key))
  }

  // An AND combo is meaningless if it stacks two one-per-order values (e.g. two colors).
  const comboImpossible = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const t of staging) {
      if (SCALAR_DIMS.has(t.dimension)) counts[t.dimension] = (counts[t.dimension] ?? 0) + 1
    }
    return Object.values(counts).some(n => n >= 2)
  }, [staging])

  const entryLabel = (e: VocabEntry) => {
    const d = cleanDesc(e.code, e.description)
    return d ? `${e.code} · ${d}` : e.code
  }

  return (
    <div className="border border-[#E5E7EC] bg-[#FAFBFC] p-5 space-y-4">
      {/* Active rules */}
      {blacklist.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {blacklist.map(r => (
            <span key={ruleKey(r)} className="inline-flex items-center gap-1.5 border border-black bg-white px-2 py-1 text-xs">
              {r.terms.map((t, i) => (
                <span key={termKey(t)} className="inline-flex items-center gap-1.5">
                  {i > 0 && <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-bmw-blue-grey">and</span>}
                  <span>
                    <span className="text-[9px] font-bold uppercase tracking-[0.06em] text-[#999]">{DIM_LABEL[t.dimension]}</span>{' '}
                    <span className="font-mono text-black">{t.value}</span>
                  </span>
                </span>
              ))}
              <button
                type="button" onClick={() => removeRule(ruleKey(r))} title="Remove rule"
                className="ml-1 text-[#999] hover:text-black"
              >
                ✕
              </button>
            </span>
          ))}
          <button
            type="button" onClick={() => onChange([])}
            className="text-xs text-[#666] hover:text-black underline underline-offset-2"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Staging row — the values being selected */}
      <div className="flex flex-wrap items-center gap-2 border border-dashed border-[#CFD3DB] bg-white px-3 py-2.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#777]">Selected</span>
        {staging.length === 0 ? (
          <span className="text-xs text-[#999]">Click values below to select them, then choose how to exclude.</span>
        ) : (
          staging.map((t, i) => (
            <span key={termKey(t)} className="inline-flex items-center gap-1.5 border border-bmw-ocean bg-white px-2 py-0.5 text-xs">
              {i > 0 && <span className="text-[#CFD3DB] select-none">·</span>}
              <span>
                <span className="text-[9px] font-bold uppercase tracking-[0.06em] text-[#999]">{DIM_LABEL[t.dimension]}</span>{' '}
                <span className="font-mono text-black">{t.value}</span>
              </span>
              <button type="button" onClick={() => toggleStaging(t.dimension, t.value)} className="text-[#999] hover:text-black">✕</button>
            </span>
          ))
        )}
        <div className="ml-auto flex items-center gap-2">
          {staging.length > 0 && (
            <button type="button" onClick={() => setStaging([])} className="text-xs text-[#999] hover:text-black">Clear</button>
          )}
          {staging.length >= 2 ? (
            <>
              <button
                type="button" onClick={addSeparate}
                title="Exclude every selected value on its own"
                className="border border-bmw-ocean bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] text-bmw-ocean hover:bg-[#F3F6FB]"
              >
                Exclude each
              </button>
              <button
                type="button" onClick={addCombo} disabled={comboImpossible}
                title={comboImpossible
                  ? "A car has only one color / interior / rim, so two of the same category can't occur together"
                  : 'Exclude only cars that have all selected values at once'}
                className="bg-bmw-ocean px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] text-white hover:bg-[#024556] disabled:opacity-30 disabled:hover:bg-bmw-ocean"
              >
                Only together
              </button>
            </>
          ) : (
            <button
              type="button" disabled={!staging.length} onClick={addSeparate}
              className="bg-bmw-ocean px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] text-white disabled:opacity-30"
            >
              Add to blacklist
            </button>
          )}
        </div>
        {staging.length >= 2 && (
          <p className="w-full text-[11px] text-[#999]">
            <span className="font-bold text-[#666]">Exclude each</span> removes every selected value on its own (independently).{' '}
            {comboImpossible
              ? <span className="text-[#666]">Two values of the same category can’t occur on one car, so “Only together” doesn’t apply.</span>
              : <><span className="font-bold text-[#666]">Only together</span> removes just cars that have all of them at once.</>}
          </p>
        )}
      </div>

      {/* Search + browse */}
      <input
        type="text" value={query} onChange={e => setQuery(e.target.value)}
        placeholder="Search by code or name…"
        className="w-full border border-[#CFD3DB] bg-white px-3 py-2 text-sm text-black focus:border-bmw-ocean focus:outline-none rounded-[4px]"
      />
      {err && <p className="text-xs text-black">⚠ {err}</p>}
      {!vocab && !err && <p className="text-xs text-[#999]">Loading options…</p>}
      {filtered && filtered.length === 0 && <p className="text-xs text-[#999]">No matches.</p>}
      {filtered && filtered.length > 0 && (
        <div className="max-h-80 space-y-4 overflow-y-auto pr-1">
          {filtered.map(({ dim, label, entries }) => (
            <div key={dim}>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#777]">
                {label} <span className="text-[#BBB]">· {entries.length}</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {entries.map(e => {
                  const active = stagingKeys.has(`${dim}:${e.code}`)
                  const desc = cleanDesc(e.code, e.description)
                  return (
                    <button
                      key={e.code} type="button"
                      onClick={() => toggleStaging(dim, e.code)}
                      title={entryLabel(e)}
                      className={`max-w-full truncate border px-2 py-1 text-xs transition ${
                        active
                          ? 'border-bmw-ocean bg-bmw-ocean text-white'
                          : 'border-[#CFD3DB] bg-white text-black hover:border-black'
                      }`}
                    >
                      <span className="font-mono">{e.code}</span>
                      {desc && <span className={active ? 'text-white/80' : 'text-[#999]'}> · {desc}</span>}
                      <span className={active ? 'text-white/70' : 'text-[#BBB]'}> · {e.count}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
